export interface ISearchFlightsRequest {
    date: string
    backDate?: string
    departure: string
    arrival: string
    passengers: IPassenger[]
    classOfService: string
    directOnly: boolean
    depIsCity: boolean
    arrIsCity: boolean
}

export interface IPassenger {
    type: TPassengerType
    count: number
}

export interface ISearchFlightsResponse {
    flights?: ISearchFlight[]
}

export interface ISearchFlight {
    id: string
    segments: ISearchFlightSegment[]
    prices: ISearchPrice[]
    dep_airport_code: string
    arr_airport_code: string
    total_duration: number
    type_info: IFlightTypeInfo
}

export interface IFlightTypeInfo {
    type: string
    multy_ow_leg: boolean
    direction_type: string
    has_supplier_virtual_inline: boolean
}

export interface ISearchFlightSegment {
    id: number
    dep_airport_code: string
    arr_airport_code: string
    dep_utc_offset: number
    arr_utc_offset: number
    dep_terminal: string
    arr_terminal: string
    dep_date_time: string
    arr_date_time: string
    flight_number: string
    op_airline: string
    marketing_airline: string
    aircraft_type: string
    flight_time: number
    e_ticket: boolean
}

export interface ISearchPrice {
    id: number
    refundable: boolean
    ticket_time_limit: string
    validating_company: string
    private_fare_ind: boolean
    fares: IFare[]
    booking_classes: ISearchBookingClass[]
}

export interface ISearchBookingClass {
    segment_number: number
    base_class: string
    booking_class_code: string
    free_seat_count: number
    meal_type: string
    cabin: string
}

export interface IFare {
    type: TPassengerType
    quantity: number
    total_fare: number
    taxes: number
    currency: string
}

export interface IAddOpsResponse {
    seat_map: ISeatMap
    actualized_flight: IFlight
    fare_families_flights: IFlight[]
}

export interface IFlight {
    id: string
    segments: IFlightSegment[]
    prices: IPrice[]
    dep_airport_code: string
    arr_airport_code: string
    total_duration: number
    mandatory_latin_names: boolean
    expected_ticket_count: number
    type_info: IFlightTypeInfo
    fare_families_descs: IFareFamilyDescription[]
    business_rules: IBusinessRule[]
}

export interface IPrice {
    id: number
    refundable: boolean
    ticket_time_limit: string
    validating_company: string
    private_fare_ind: boolean
    fares: IFare[]
}

export interface IFlightSegment {
    id: number
    dep_airport_code: string
    arr_airport_code: string
    dep_utc_offset: number
    arr_utc_offset: number
    dep_terminal: string
    arr_terminal: string
    dep_date_time: string
    arr_date_time: string
    flight_number: string
    op_airline: string
    marketing_airline: string
    aircraft_type: string
    flight_time: number
    e_ticket: boolean
    booking_class: IBookingClass
    supplier_info: ISupplierInfo
    stop_points: IStopPoint[]
}

export interface IStopPoint {
    airport_code: string
    city_code: string
    utc: string
    terminal: string
    arr_date_time: string
    dep_date_time: string
}

export interface IBookingClass {
    base_class: string
    booking_class_code: string
    free_seat_count: number
    meal_type: string
    cabin: string
}

export interface ISupplierInfo {
    status: string
    generalized_status: string
}

export interface IFareFamilyDescription {
    id: number
    name: string
    carry_on: ICarry

    params: IFareFamilyParameter[]
    free_meal: string[]
    refundable: boolean
}

export interface ICarry {
    value: number
    measure: string
}

export interface IFareFamilyParameter {
    code: string
    short_description: IFareFamilyParameterLang[]
    full_description: IFareFamilyParameterLang[]
    need_to_pay: string
    priority: number
}

export interface IFareFamilyParameterLang {
    code: string
    value: string
}

export interface IBusinessRule {
    id: number
    add_params: IBusinessRuleParam[]
}

export interface IBusinessRuleParam {
    name: string
    value: string
}

export interface ISeatMap {
    segments: ISeatMapSegment[]
}

export interface ISeatMapSegment {
    segment_number: number
    floors: ICabinFloor[]
}

export interface ICabinFloor {
    is_upper: boolean
    rows: ISeatRow[]
}

/**
 * Карта мест из `GET /aviabooking/flights/:id/add-ops`. Опциональные поля ниже —
 * те, которых в ответе нет: сервер присылает место как
 * `{number, type, is_free, not_exists, price, ancillary_service}`. Раньше они были
 * объявлены обязательными, и обращение к `characteristics` роняло рендер карты мест.
 */
export interface ISeatRow {
    number: number
    /** Отсутствует в ответе add-ops. Читать только через `lib/seat-characteristics`. */
    characteristics?: string
    /** Наличие в ответе не подтверждено; читателей в коде нет. */
    cabin_code?: string
    seats: ISeat[]
}

export interface ISeatPrice {
    amount: number
    currency: string
}

export interface ISeat {
    number: string
    type: string
    /** Отсутствует в ответе add-ops. Читать только через `lib/seat-characteristics`. */
    characteristics?: string
    is_free: boolean
    not_exists: boolean
    price: ISeatPrice
    /** Отсутствует в ответе add-ops — в тело брони уходит с фолбэком на пустую строку. */
    rfisc?: string
    ancillary_service: ISeatAncillaryService
}

export interface ISeatAncillaryService {
    id: number
    name: string
    group: string
    sub_group: string
    type: string
    rfic: string
    rfisc: string
    ssr_code: string
    ssr_text: string
    ssr_description: string
    ssr_description_required: boolean
    company_code: string
    emd_type: string
    prices: ISeatAncillaryServicePrice[]
}

export interface ISeatAncillaryServicePrice {
    segment_number: number
    passenger_types: TPassengerType[]
    amount: number
    currency: string
}

export interface IGetAirportsRequest {
    language: string
    code: string
    countryCode: string
}

export interface IAirport {
    code: string
    name: string
    city: string
    country_code: string
}

export interface IGetCountriesRequest {
    language: string
    countryCode: string
}

export interface ICountry {
    code: string
    name: string
}

export interface IGetAirlinesRequest {
    code: string
    language: string
}

export interface IAirline {
    code: string
    name: string
    country_code: string
    logo: string
}

export interface IGetAircraftRequest {
    language: string
    code: string
}

export interface IAircraft {
    code: string
    name: string
}

export interface BookFlightRequest {
    phone: string
    travellers: IBookTraveller[]
    data_items: IBookDataItem[]
    ancillary_services?: IAncillaryService[]
}

export interface IAncillaryService {
    group: string
    rfic: string
    rfisc: string
    type: string
    traveller_ref: number
    segment_refs?: number[]
    quantity: number
    emd_type: string
    is_free: boolean
}

export interface IBookTraveller {
    id: number
    type: TPassengerType
    first_name: string
    last_name: string
    middle_name?: string
    date_of_birth: string
    nationality: string
    gender: TGenderType
    save_document?: boolean
}

export interface IBookDataItem {
    traveller_ref: number
    segment_ref?: number
    id_document?: IDocumentDTO
    booked_seat?: {
        number: string
        rfisc: string
    }
}

export interface IDocumentDTO {
    type: DocumentType
    number: string
    issue_country_code: string
    expired_at?: string
    issued_at?: string
}

export interface ISavedDocument {
    id: string
    first_name: string
    last_name: string
    middle_name?: string
    date_of_birth: string
    nationality: string
    gender: TGenderType
    id_document: IDocumentDTO
}

export const PassengerType = {
    "ADT": "ADT", // взрослый — пассажир старше 12-ти лет (по умолчанию);
    "CNN": "CNN", //  ребёнок — пассажир старше 2-х и младше 12-ти лет;
    "INF": "INF" // младенец — пассажир младше 2-х лет, без места в самолёте;
} as const

export type TPassengerType = typeof PassengerType[keyof typeof PassengerType]

export const ClassOfService = {
    "Economy": "Economy",
    "Business": "Business",
    "First": "First",
    "PremiumEconomy": "PremiumEconomy",
    "All": "All"
} as const

export type TClassOfService = typeof ClassOfService[keyof typeof ClassOfService]

export const GenderType = {
    Male: "M",
    Female: "F"
} as const

export type TGenderType = typeof GenderType[keyof typeof GenderType]

export enum BookingStatus {
    StatusBooked = 1,
    StatusCanceled = 2,
    StatusTicketed = 3,
    StatusAwaitingTicketing = 4,
    StatusPartial = 5,
    StatusRequested = 6,
    StatusRejected = 7,
    StatusProblematic = 8,
}

export interface IBooking {
    status: BookingStatus
    booking_id: string
    created_at: string
    admin_cancel_available?: boolean
    admin_cancel_unavailable_reason?: string | null
    admin_cancel_deadline?: string | null
    refund_status?: number | null
    refund_id?: string | null
    warnings?: string[]
    possible_actions?: string[]
    segments: IFlightSegment[]
    price_summary: {
        total_fare: number
        taxes: number
        currency: string
    }
    travellers: ITraveller[]
    price_changed: boolean
    price_prev: number | null
    order_status: number | null
}

export interface ITraveller {
    id: number
    type: TPassengerType
    last_name: string
    first_name: string
    middle_name: string
    date_of_birth: string
    nationality: string
    gender: string
}

export interface IListBookingsResponse {
    bookings: {
        book: IBooking
        updated_at: string
    }[]
    total_pages: number
    total_count: number
}

export interface IBookingPaymentResponse {
    detail: "paid" | "already_paid"
}

export interface IBonusBalanceResponse {
    bonus_balance: number
}

export enum DocumentType {
    Unspecified = "",
    BerthRegDocument = "BerthRegDocument",
    InternationalPassportRU = "InternationalPassportRU",
    Passport = "Passport"
}

export interface IGetTicketsResponse {
    tickets: ITicket[]
}

export interface ITicket {
    url: string
}